export type QuoteBuilderSectionId = 'details' | 'items' | 'payment'

export type QuoteBuilderReadinessIssue = {
  step: QuoteBuilderSectionId
  message: string
}

export function getQuoteBuilderReadinessIssues({
  title,
  itemCount,
  paymentScheduleIssueMessage,
}: {
  title: string
  itemCount: number
  paymentScheduleIssueMessage?: string
}): QuoteBuilderReadinessIssue[] {
  const issues: QuoteBuilderReadinessIssue[] = []

  if (!title.trim()) {
    issues.push({ step: 'details', message: 'Add a quote title.' })
  }
  if (itemCount === 0) {
    issues.push({ step: 'items', message: 'Add at least 1 line item.' })
  }
  if (paymentScheduleIssueMessage) {
    issues.push({ step: 'payment', message: paymentScheduleIssueMessage })
  }

  return issues
}

export type QuoteBuilderChecklistItem = {
  id: 'title' | 'contents' | 'schedule' | 'stripe'
  /** Left-column card the row focuses when the check is not satisfied. */
  section?: QuoteBuilderSectionId
  label: string
  satisfied: boolean
}

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

/**
 * "Before you send" checklist shown in the quote builder summary rail.
 *
 * Title/line-item/payment-schedule readiness comes from
 * `getQuoteBuilderReadinessIssues` so the checklist and the submit-time
 * validation can never disagree. Stripe connection is layered on top: it does
 * not block saving a draft, only sending a paid quote.
 */
export function getQuoteBuilderChecklist({
  title,
  itemCount,
  eventCount,
  paymentScheduleIssueMessage,
  isStripeConnected,
}: {
  title: string
  itemCount: number
  eventCount: number
  paymentScheduleIssueMessage?: string
  isStripeConnected: boolean
}): QuoteBuilderChecklistItem[] {
  const issues = getQuoteBuilderReadinessIssues({ title, itemCount, paymentScheduleIssueMessage })
  const hasIssue = (step: QuoteBuilderSectionId) => issues.some((issue) => issue.step === step)

  return [
    {
      id: 'title',
      section: 'details',
      label: hasIssue('details') ? 'Add a title' : 'Title added',
      satisfied: !hasIssue('details'),
    },
    {
      id: 'contents',
      section: 'items',
      label: `${pluralize(itemCount, 'line item')} · ${pluralize(eventCount, 'event')}`,
      satisfied: !hasIssue('items'),
    },
    {
      id: 'schedule',
      section: 'payment',
      label: hasIssue('payment')
        ? (paymentScheduleIssueMessage as string)
        : 'Schedule covers 100% of total',
      satisfied: !hasIssue('payment'),
    },
    {
      id: 'stripe',
      label: isStripeConnected ? 'Stripe connected' : 'Stripe not connected',
      satisfied: isStripeConnected,
    },
  ]
}
